/* eslint-disable */

import { Injectable } from '@angular/core';
import { LoggerService } from '../logger.service';
import { UtilsService } from '../utils.service';
import { MatchersService } from './matchers.service';
import { MSRemoteLoggerCategory } from 'src/app/adapters/remote-logger/utils/remote-logger.utils';

@Injectable({
  providedIn: 'root'
})
export class MatcherService {
  constructor(
    private logger: LoggerService,
    private utils: UtilsService,
    private matchers: MatchersService
  ) {}

  conditionMatches(surveyData, condition, toggleIndex, matchNulls) {
    const self = this;
    const checkFn = function () {
      const values = condition.fn;
      const fnName = Array.isArray(values) ? values[0] : values;
      const notAvailableMsg = 'Fn N/A: ' + fnName;

      if (!self.matchers[fnName]) {
        self.logger.error(notAvailableMsg, MSRemoteLoggerCategory.SURVEY_MATCHER_SERVICE, condition);
        throw notAvailableMsg;
      }

      // if we have a toggle index, pass it along
      // toggle index can come from multiQMatchCount question. need to pass a null to multiQMatchCount when toggleIndex is exhausted
      // so if we're using 'personMeetsCondition' function, once you run out of people to check, pass back null
      if (toggleIndex !== undefined) {
        return self.matchers[fnName].apply(self.matchers, [surveyData].concat(values.slice(1), toggleIndex));
      } else if (Array.isArray(values)) {
        // e.g.: "{fn: ['tripNumDivisibleBy', 5]}" calls helper.tripNumDivisibleBy(surveyData, 5);
        return self.matchers[fnName].apply(self.matchers, [surveyData].concat(values.slice(1)));
      } else {
        // e.g. "{fn: 'isEmployed'}" (no params to pass)
        return self.matchers[fnName].apply(self.matchers, [surveyData]);
      }
    };
    const checkField = function () {
      const value = condition.is;
      const values = condition['in'];
      let field = condition.field;
      const comparators = ['is', 'in'];
      let isCbWithToggleIndex = false;
      let lap;
      const compCount = comparators.reduce(function (count, key) {
        return count + (key in condition ? 1 : 0);
      }, 0);

      //HACK, special case to show/hide toggle item based on idx
      if (field === 'toggleIndex') {
        if ('is' in condition) {
          return toggleIndex === value;
        } else if ('in' in condition) {
          return values.indexOf(toggleIndex) !== -1;
        }
      }

      if (compCount === 0 && '_CB_' + field + '_0' in surveyData) {
        // should always have a zeroth element, preventing throw
        isCbWithToggleIndex = true;
      } else if (compCount !== 1) {
        throw 'Exactly 1 comparator expected (' + this.utils.toJson(condition) + ')';
      }

      // HACK_LAP check field conditions w/ a lapOffset (e.g. hide: {field: q1, lapOffset: -1, is: 1})
      if (condition.lapOffset !== undefined) {
        if (!Number.isInteger(surveyData.lap)) {
          throw new Error('lapOffset outside loop ' + condition);
        }
        if (condition.lapOffset > 0) {
          throw new Error('Checking future laps (lapOffset > 0) not allowed ' + condition);
        }
        lap = condition.lapOffset + surveyData.lap;
        if (lap < 0) {
          // not throwing - ignoring so previous laps can be checked without guarding against no previous laps
          //this.logger.warn("lap negative: " + condition.lapOffset + ' + ' + surveyData.lap + ' < 0');
          return null;
        }
        field = field + 'L' + lap;
      }

      if (toggleIndex !== undefined) {
        if (
          !(
            field in surveyData ||
            (compCount === 0 && '_CB_' + field + '_' + toggleIndex in surveyData) ||
            (compCount > 0 && '_CB_' + field + '_' + value in surveyData) ||
            field + '_' + toggleIndex in surveyData
          )
        ) {
          return null;
        }
      }

      // HACK_CB
      //   alternative: get question[s] using question name and get field names from that
      if ('is' in condition) {
        return (
          surveyData[field] === value ||
          surveyData['_CB_' + field + '_' + value] === 1 ||
          (toggleIndex !== undefined &&
            (surveyData[field + '_' + toggleIndex] === value || (matchNulls && surveyData[field + '_' + toggleIndex] === null)))
        );
      } else if ('in' in condition) {
        let matched = false;

        if (values.indexOf(surveyData[field]) !== -1) {
          matched = true;
        }

        values.forEach((value) => {
          if (surveyData['_CB_' + field + '_' + value] === 1) {
            matched = true;
          }
        });

        if (toggleIndex !== undefined) {
          if (values.indexOf(surveyData[field + '_' + toggleIndex]) !== -1) {
            //self.logger.log('checkField toggled in', field + '_' + toggleIndex, surveyData[field + '_' + toggleIndex])
            matched = true;
          }
          if (matchNulls && surveyData[field + '_' + toggleIndex] === null) {
            //self.logger.log('checkField toggled null', field + '_' + toggleIndex, surveyData[field + '_' + toggleIndex])
            matched = true;
          }
        }

        return matched;
      } else if (isCbWithToggleIndex) {
        return (
          1 === surveyData['_CB_' + field + '_' + toggleIndex] || (matchNulls && surveyData['_CB_' + field + '_' + toggleIndex] === null)
        );
      }
    };
    let matches;

    if ('fn' in condition) {
      // for helper function
      matches = checkFn();
    } else if ('field' in condition) {
      matches = checkField();
    } else if ('boolean' in condition) {
      matches = condition.boolean === true; // boolean expected
    } else {
      throw 'Unhandled condition "' + this.utils.toJson(condition) + '"';
    }
    return Promise.resolve(matches);
  }

  multiQConditionsMatch(conditions, surveyData) {
    const clonedConditions = JSON.parse(JSON.stringify(conditions));
    const matchType = conditions.multiQMatchCount;
    // ALL matches nulls so unanswered fields (e.g. 3 entities with 12 max would have 9 w/ all nulls) don't cause a failure
    // warning - all nulls (e.g. 12 out of 12) returns true for ALL, unexpected?
    const matchNulls = 'ALL' === matchType;
    let trues = 0;
    let loops = 0;
    const maxLoops = 100;
    let exhausted = false;
    let index = 0;
    const self = this;
    const evaluate = function () {
      if ('ALL' === matchType) {
        if (trues < loops) {
          return false;
        } else if (exhausted) {
          return true;
        }
      } else if ('ANY' === matchType) {
        if (trues > 0) {
          return true;
        } else if (exhausted) {
          return false;
        }
      } else if ('NONE' === matchType) {
        if (trues > 0) {
          return false;
        } else if (exhausted) {
          return true;
        }
      } else if (Number.isInteger(matchType)) {
        if (trues > matchType) {
          return false;
        } else if (exhausted) {
          return trues === matchType;
        }
      }
      return null;
    };
    const loopCheck = function () {
      return self.conditionsMatch(clonedConditions, surveyData, false, false, index, matchNulls).then(function (b) {
        //self.logger.log('loopCheck', clonedConditions, index, b)
        let e;
        if (b === null) {
          exhausted = true;
          return evaluate();
        } else {
          loops++;
          if (loops > maxLoops) {
            throw 'Too much recursion (' + loops + ' loops) in helper.multiQConditionsMatch';
          }
          if (b === true) {
            trues++;
          }
          e = evaluate();
          if (e === null) {
            // not done yet
            index++;
            return loopCheck();
          } else {
            return e;
          }
        }
      });
    };

    delete clonedConditions.multiQMatchCount;
    return loopCheck();
  }

  conditionsMatch(conditions, surveyData, any?, not?, toggleIndex?, matchNulls?): Promise<boolean> {
    // any v. all (default) == OR v. AND
    const self = this;
    const promises = [];
    const reduce = function (results) {
      const isTrue = function (b) {
        return not ? !b : b;
      };
      if (results.includes(null)) {
        return null;
      }
      //logger.log('Reducing ' + results.join(', ') + ' to:' + results[any ? 'some' : 'every'](isTrue) + ' w/ any:' + any + ' not:' + not);
      return results[any ? 'some' : 'every'](isTrue);
    };
    const conditionKeys = ['any', 'all', 'field', 'fn', 'not', 'boolean'];
    const throwForBadTypeKeys = function () {
      let count = 0;
      conditionKeys.forEach(function (conditionKey) {
        if (conditionKey in conditions) {
          count++;
        }
        if (count > 1) {
          throw ['Exactly 1 of ', self.utils.toJson(conditionKeys), ' allowed (', self.utils.toJson(conditions), ')'].join('');
        }
      });
      if (count === 0) {
        throw ['Exactly 1 of ', self.utils.toJson(conditionKeys), ' expected (', self.utils.toJson(conditions), ')'].join('');
      }
    };

    toggleIndex = 'toggleIndex' in conditions ? conditions.toggleIndex : toggleIndex;

    //logger.log(conditions);
    // conditions have a single type: any, all, field, fn
    // arrays of conditions default to 'all'
    if ('multiQMatchCount' in conditions) {
      promises.push(self.multiQConditionsMatch(conditions, surveyData));
    } else if (Array.isArray(conditions)) {
      conditions.forEach(function (condition) {
        promises.push(self.conditionsMatch(condition, surveyData, false, false, toggleIndex, matchNulls));
      });
    } else {
      throwForBadTypeKeys();
      if ('any' in conditions) {
        promises.push(self.conditionsMatch(conditions.any, surveyData, true, false, toggleIndex, matchNulls));
      } else if ('all' in conditions) {
        promises.push(self.conditionsMatch(conditions.all, surveyData, false, false, toggleIndex, matchNulls));
      } else if ('not' in conditions) {
        promises.push(self.conditionsMatch(conditions.not, surveyData, false, true, toggleIndex, matchNulls));
      } else {
        promises.push(self.conditionMatches(surveyData, conditions, toggleIndex, matchNulls));
      }
    }
    return Promise.all(promises).then(reduce);
  }

  show = function (element, surveyData) {
    const self = this;
    const shown = function () {
      return element.show ? self.conditionsMatch(element.show, surveyData) : Promise.resolve(true);
    };
    const notHidden = function () {
      return element.hide
        ? self.conditionsMatch(element.hide, surveyData).then(function (match) {
            return !match;
          })
        : Promise.resolve(true);
    };

    return Promise.all([shown(), notHidden()]).then(function (results) {
      return results[0] && results[1];
    });
  };
}
